import { useEffect, useState } from 'react';
import type { ScheduledPostStatus } from './types';

/** A timer longer than this never fires in time (setTimeout keeps 32 bits); such posts wait. */
const MAX_TIMER_MS = 24 * 60 * 60 * 1000;

/**
 * The status that the app shows for a post. A scheduled post whose time has passed is going out:
 * the server queues it at its time, so the app shows it as `publishing` ("Publishing…"), never
 * "Scheduled". The realtime event `promo.post.updated` then brings `posted` or `failed`.
 */
export function displayPostStatus(
  post: { status: ScheduledPostStatus; scheduledAt: string },
  nowMs: number
): ScheduledPostStatus {
  if (post.status !== 'scheduled') {
    return post.status;
  }
  const dueMs = Date.parse(post.scheduledAt);
  return Number.isNaN(dueMs) || dueMs > nowMs ? post.status : 'publishing';
}

/** `displayPostStatus`, and a render at the time of the post when it is due later. */
export function useDisplayPostStatus(post: { status: ScheduledPostStatus; scheduledAt: string }): ScheduledPostStatus {
  const [nowMs, setNowMs] = useState(() => Date.now());
  const shown = displayPostStatus(post, nowMs);
  const dueMs = Date.parse(post.scheduledAt);

  useEffect(() => {
    // Only a post that the screen still shows as scheduled waits for its time.
    if (shown !== 'scheduled' || Number.isNaN(dueMs)) {
      return undefined;
    }
    const wait = dueMs - Date.now();
    if (wait > MAX_TIMER_MS) {
      return undefined;
    }
    const timer = setTimeout(() => setNowMs(Date.now()), Math.max(0, wait) + 50);
    return () => clearTimeout(timer);
  }, [shown, dueMs]);

  return shown;
}
